import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    Radio, 
    ArrowLeft, 
    Briefcase, 
    AlertCircle,
    CheckCircle2,
    Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const PROJECT_TYPES = [
    "Film / Movie",
    "Television / Streaming",
    "Advertising / Commercial",
    "Video Games",
    "Podcast / Audio Content",
    "Social Media / Digital Content",
    "Corporate / Brand",
    "Other"
];

export default function PosterSignupPage() {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    const [formData, setFormData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        company_name: '',
        company_website: '',
        project_type: '',
        project_description: ''
    });

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.project_type) {
            toast.error('Please select a project type');
            return;
        }
        
        setSubmitting(true);
        
        try {
            const response = await fetch(`${API}/poster/signup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            
            if (response.ok) {
                setSubmitted(true);
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Signup failed');
            }
        } catch (error) {
            toast.error('Error submitting request');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#121212] flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-md w-full bg-[#1A1A1A] border border-[#333] rounded-2xl p-8 text-center"
                >
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-8 h-8 text-green-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-3">Request Submitted!</h1>
                    <p className="text-[#A3A3A3] mb-6">
                        Thank you for your interest. Our team will review your application and get back to you within 24-48 hours.
                    </p>
                    <Link to="/login">
                        <Button className="bg-[#F51E1E] hover:bg-[#D41212] text-white">
                            Return to Login
                        </Button>
                    </Link>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#121212] flex">
            {/* Left side - Form */}
            <div className="flex-1 flex items-center justify-center p-8">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="w-full max-w-lg"
                >
                    {/* Back button */}
                    <Link 
                        to="/login" 
                        className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-white mb-8 transition-colors"
                    >
                        <ArrowLeft size={18} />
                        <span>Back to login</span>
                    </Link>

                    {/* Logo */}
                    <div className="flex items-center gap-3 mb-8">
                        <div className="w-12 h-12 rounded-xl bg-[#F51E1E] flex items-center justify-center">
                            <Radio size={26} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-white">Pitch Room Sync Portal</h1>
                            <p className="text-sm text-[#A3A3A3]">Poster Registration</p>
                        </div>
                    </div>

                    {/* Alert */}
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
                        <div className="flex gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-amber-200 font-medium text-sm">For Project Owners Only</p>
                                <p className="text-amber-200/70 text-sm mt-1">
                                    This signup is for companies and individuals who need music for their projects. 
                                    If you're an artist or producer looking to submit music, please contact an admin for an invite.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-white mb-2 block">First Name *</Label>
                                <Input
                                    value={formData.first_name}
                                    onChange={(e) => handleChange('first_name', e.target.value)}
                                    required
                                    className="bg-[#262626] border-[#404040] text-white"
                                    placeholder="John"
                                />
                            </div>
                            <div>
                                <Label className="text-white mb-2 block">Last Name *</Label>
                                <Input
                                    value={formData.last_name}
                                    onChange={(e) => handleChange('last_name', e.target.value)}
                                    required
                                    className="bg-[#262626] border-[#404040] text-white"
                                    placeholder="Doe"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-white mb-2 block">Email *</Label>
                            <Input
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                required
                                className="bg-[#262626] border-[#404040] text-white"
                                placeholder="john@company.com"
                            />
                        </div>

                        <div>
                            <Label className="text-white mb-2 block">Company / Organization *</Label>
                            <Input
                                value={formData.company_name}
                                onChange={(e) => handleChange('company_name', e.target.value)}
                                required
                                className="bg-[#262626] border-[#404040] text-white"
                                placeholder="Acme Productions"
                            />
                        </div>

                        <div>
                            <Label className="text-white mb-2 block">Company Website (optional)</Label>
                            <Input
                                type="url"
                                value={formData.company_website}
                                onChange={(e) => handleChange('company_website', e.target.value)}
                                className="bg-[#262626] border-[#404040] text-white"
                                placeholder="https://company.com"
                            />
                        </div>

                        <div>
                            <Label className="text-white mb-2 block">Project Type *</Label>
                            <Select
                                value={formData.project_type}
                                onValueChange={(value) => handleChange('project_type', value)}
                            >
                                <SelectTrigger className="bg-[#262626] border-[#404040] text-white">
                                    <SelectValue placeholder="Select project type" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#262626] border-[#404040]">
                                    {PROJECT_TYPES.map((type) => (
                                        <SelectItem 
                                            key={type} 
                                            value={type}
                                            className="text-white hover:bg-[#404040]"
                                        >
                                            {type}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label className="text-white mb-2 block">Tell us about your sync needs *</Label>
                            <Textarea
                                value={formData.project_description}
                                onChange={(e) => handleChange('project_description', e.target.value)}
                                required
                                rows={4}
                                className="bg-[#262626] border-[#404040] text-white resize-none"
                                placeholder="Describe the types of projects you work on and why you need music licensing..."
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full h-12 bg-[#F51E1E] hover:bg-[#D41212] text-white font-semibold rounded-xl"
                        >
                            {submitting ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    <Briefcase className="w-5 h-5 mr-2" />
                                    Submit Application
                                </>
                            )}
                        </Button>
                    </form>
                </motion.div>
            </div>

            {/* Right side - Image/Info */}
            <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#F51E1E]/20 to-[#121212] items-center justify-center p-8">
                <div className="max-w-md text-center">
                    <h2 className="text-3xl font-bold text-white mb-4">
                        Find the Perfect Sound for Your Project
                    </h2>
                    <p className="text-[#A3A3A3] text-lg mb-8">
                        Connect with talented artists and producers. Post your sync opportunities and receive curated submissions from our community.
                    </p>
                    <div className="space-y-4 text-left">
                        {[
                            "Post detailed project briefs",
                            "Receive quality submissions from vetted artists",
                            "Review and manage submissions with our Kanban board",
                            "Direct communication with artists"
                        ].map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-3">
                                <CheckCircle2 className="w-5 h-5 text-[#F51E1E]" />
                                <span className="text-white">{feature}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
